// Empate radio ↔ unidad vía el conductor (elemento) asignado en
// attributes.conductor de cada device. Sin dependencias de Vue.
//
// Dos fuentes, porque el mapa NO contiene todos los devices: /positions solo
// devuelve devices con posición conocida por el server (~2K de ~8K), y las
// unidades del elemento suelen estar offline sin posición → nunca se vuelven
// monitors. Por eso el empate fusiona:
//   1. Monitors vivos (window.__mapV2Monitors) → filas clickeables (ir al mapa).
//   2. Catálogo completo de GET /devices (alimentado por MapV2._ingestEntities
//      vía updateDeviceCatalog) → filas informativas sin posición en mapa.
// Bidireccional: al ver un radio lista las unidades del elemento y viceversa.
import { isRadioDevice } from './radio-device'

// Valores basura observados en producción para attributes.conductor
// ('', '#N/D', ids de drivers borrados). Los ids borrados no se pueden
// detectar aquí (requeriría el catálogo de drivers), pero el empate por id
// compartido sigue siendo válido aunque el driver ya no exista.
const INVALID_CONDUCTORS = ['#N/D', 'N/D', 'N/A', 'NA', 'NULL', 'UNDEFINED', 'S/N', '0']

// Conductor normalizado (string) o null si viene vacío o es basura.
export function normalizeConductor(value) {
  if (value === undefined || value === null) return null
  const s = String(value).trim()
  if (!s) return null
  if (INVALID_CONDUCTORS.indexOf(s.toUpperCase()) !== -1) return null
  return s
}

// Conductor del raw de un device, ya normalizado.
export function getDeviceConductor(raw) {
  const c = raw && raw.attributes ? raw.attributes.conductor : undefined
  return normalizeConductor(c)
}

// ¿El raw es un radio? Categoria radioType (fuente de verdad) con fallback
// al uniqueId TETRA_* — misma heurística que usa MapDrawer para los badges.
function isRadioRaw(raw) {
  if (isRadioDevice(raw)) return true
  const uid = (raw && raw.uniqueId) || ''
  return String(uid).indexOf('TETRA') !== -1
}

// ——— Catálogo de devices con conductor (deviceId → entry slim) ———
// Solo guardamos devices CON conductor: el catálogo existe únicamente para
// resolver empates, no es un espejo de /devices (8K+ items → ~1.5K entries).
let catalogById = {}

// Ingesta del catálogo. La llaman _ingestEntities (boot: GET /devices trae
// TODOS los devices, incluso sin posición) y _updateEntitiesFromWS (updates
// en vivo). Un raw sin objeto attributes se ignora (updates parciales de WS
// no deben borrar entries); attributes presente sin conductor válido sí
// remueve (desasignación real del conductor).
export function updateDeviceCatalog(items) {
  if (!Array.isArray(items)) return
  let changed = 0
  for (let i = 0; i < items.length; i++) {
    const it = items[i]
    if (it && it.id != null && it.attributes) {
      const conductor = getDeviceConductor(it)
      if (conductor) {
        catalogById[it.id] = {
          deviceId: it.id,
          title: it.name || it.uniqueId || String(it.id),
          uniqueId: String(it.uniqueId || ''),
          status: it.status || 'offline',
          conductor,
          radio: isRadioRaw(it),
        }
        changed++
      } else if (catalogById[it.id]) {
        delete catalogById[it.id]
        changed++
      }
    }
  }
  // Avisar a los consumidores (MapDrawer) que el universo de empates cambió.
  // Crítico en el boot con selección persistida: el drawer monta y calcula
  // ANTES de que GET /devices llegue; sin este evento la sección "Equipo del
  // elemento" queda vacía hasta re-seleccionar.
  if (changed > 0 && typeof window !== 'undefined' && window.dispatchEvent) {
    window.dispatchEvent(new CustomEvent('kalan:conductor-catalog-updated'))
  }
}

// Id del elemento (conductor) que opera el radio identificado como `ident`
// en la red TETRA. Devuelve null si no se resuelve.
//
// `ident` es lo que el backend manda como hablante en una transmisión, y no
// siempre alcanza para identificar a nadie: en modo CANAL el backend TETRA no
// envía el id del radio y el panel cae a un genérico "Radio" (ver
// RadioTetraPanel._pushRxEntry). Por eso esto devuelve null sin ruido cuando
// no hay empate — quien llama debe tener su propio fallback.
//
// Se busca contra el catálogo de devices ya cargado, primero por uniqueId
// (identificador de red) y después por nombre visible, que es lo que el
// backend suele mandar en llamadas directas (peerName).
export function findConductorByRadioIdent(ident) {
  if (ident === undefined || ident === null) return null
  const needle = String(ident).trim().toUpperCase()
  if (!needle || needle === 'RADIO' || needle === 'PEER') return null
  const keys = Object.keys(catalogById)
  for (let i = 0; i < keys.length; i++) {
    const e = catalogById[keys[i]]
    if (!e || !e.radio) continue
    if (String(e.uniqueId || '').toUpperCase() === needle) return e.conductor
  }
  for (let j = 0; j < keys.length; j++) {
    const e = catalogById[keys[j]]
    if (!e || !e.radio) continue
    if (String(e.title || '').trim().toUpperCase() === needle) return e.conductor
  }
  return null
}

export function resetDeviceCatalog() {
  catalogById = {}
}

export function catalogSize() {
  return Object.keys(catalogById).length
}

// Orden: online primero, luego los que están en el mapa, luego alfabético.
function sortItems(list) {
  list.sort((a, b) => {
    const aOn = a.status === 'online'
    const bOn = b.status === 'online'
    if (aOn !== bOn) return aOn ? -1 : 1
    if (a.onMap !== b.onMap) return a.onMap ? -1 : 1
    return String(a.title || '').localeCompare(String(b.title || ''))
  })
  return list
}

// Busca los demás devices que comparten conductor con el monitor dado,
// fusionando monitors vivos y catálogo. Devuelve null si el monitor no es
// device, no tiene conductor válido o no hay ningún empate. Si hay:
//   { conductorId, radios: [item], unidades: [item] }
// donde item = { key, title, status, onMap, monitor|null }.
export function findConductorLinks(monitor, monitors) {
  if (!monitor || monitor.entityType !== 'device') return null
  const conductorId = getDeviceConductor(monitor.data)
  if (!conductorId) return null
  const selfDeviceId = monitor.data && monitor.data.id

  const items = []
  const liveDeviceIds = {}

  // 1. Monitors vivos → clickeables (tienen posición en el mapa).
  const keys = Object.keys(monitors || {})
  for (let i = 0; i < keys.length; i++) {
    const mon = monitors[keys[i]]
    const matches = mon && mon.entityType === 'device' && mon.id !== monitor.id
      && getDeviceConductor(mon.data) === conductorId
    if (matches) {
      const rawId = mon.data && mon.data.id
      if (rawId != null) liveDeviceIds[rawId] = true
      items.push({
        key: mon.id,
        title: mon.title || (mon.data && mon.data.name) || '',
        status: mon.status || 'offline',
        radio: isRadioRaw(mon.data),
        onMap: true,
        monitor: mon,
      })
    }
  }

  // 2. Catálogo → devices sin posición en mapa (informativos, no clickeables).
  const catKeys = Object.keys(catalogById)
  for (let j = 0; j < catKeys.length; j++) {
    const entry = catalogById[catKeys[j]]
    const matches = entry.conductor === conductorId
      && entry.deviceId !== selfDeviceId && !liveDeviceIds[entry.deviceId]
    if (matches) {
      items.push({
        key: 'catalog-' + entry.deviceId,
        title: entry.title,
        status: entry.status,
        radio: entry.radio,
        onMap: false,
        monitor: null,
      })
    }
  }

  if (!items.length) return null
  const radios = items.filter((it) => it.radio)
  const unidades = items.filter((it) => !it.radio)
  return {
    conductorId,
    radios: sortItems(radios),
    unidades: sortItems(unidades),
  }
}
